import { FC, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import ValidationError from "@components/ValidationError/ValidationError";
import { useDispatch } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import ReactCodeInput from "react-code-input";
import { useRedeemInvitationMutation } from "@store/invitationApi";
import { useNavigate } from "react-router-dom";
import { setMessage } from "@store/snackbarSlice";
import { setUser } from "@store/authSlice";
import { DeviceIdService } from "@service/device-id.service";
import { AuthService } from "@service/auth.service";
import { useGetAppConfigQuery } from "@store/appConfigApi";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import PublicForm from "@components/PublicForm";
import CustomLabelInput from "@components/CustomLabelInput";
import TermsOfService from "@components/TermsOfService";

const authService = AuthService.instance;
const deviceIdService = DeviceIdService.instance;

type InviteFormValues = {
  checkString: string;
  code?: string;
};

const InviteForm: FC<{ code?: string | null }> = ({ code }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [redeemInvitation] = useRedeemInvitationMutation();
  const { data: appConfig } = useGetAppConfigQuery();

  const codeFromUrl = code ?? undefined;
  const showCodeInput = !codeFromUrl;

  const {
    control,
    handleSubmit,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm<InviteFormValues>({
    mode: "onChange",
    criteriaMode: "all",
    defaultValues: { code: "" },
  });

  const handleSubmitError = useCallback(
    (error?: FetchBaseQueryError | SerializedError): void => {
      let errorType = "unknown";
      switch ((error as any)?.data?.errorCode) {
        case "InvitationNotFound": {
          errorType = "invalidInvitationError";
          break;
        }
        case "InvalidInvitationCheckString": {
          errorType = "invalidInvitationCheckString";
          break;
        }
      }
      dispatch(
        setMessage({ content: t(`errors.${errorType}`), severity: "error" }),
      );
    },
    [dispatch, t],
  );

  const submit = useCallback(
    async ({ checkString, code: formCode }: InviteFormValues) => {
      const invitationCode = codeFromUrl ?? formCode ?? "";
      const { data, error } = await redeemInvitation({
        checkString,
        code: invitationCode,
        deviceId: deviceIdService.getDeviceId(),
      });

      if (error || !data) {
        handleSubmitError(error);
      } else {
        authService.setAccessToken(data.accessToken);
        dispatch(
          setUser({
            id: data.user.id,
            username: "",
            email: "",
            verified: true,
            firstName: data.user.firstName || "",
            lastName: data.user.lastName || "",
            isPatient: true,
          }),
        );
        navigate("/invite-success");
      }
    },
    [redeemInvitation, dispatch, navigate, codeFromUrl, handleSubmitError],
  );

  return (
    <PublicForm
      title={t("invite.title", { appTitle: process.env.REACT_APP_APP_NAME })}
      subtitle={
        <Trans
          i18nKey="invite.subtitle"
          values={{
            appTitle: process.env.REACT_APP_APP_NAME,
          }}
          components={{ italic: <i />, bold: <strong />, br: <br /> }}
        />
      }
    >
      <PublicForm.PublicFormContainer
        onSubmit={(e) => {
          clearErrors();
          return handleSubmit(submit)(e);
        }}
      >
        <Stack spacing={2}>
          {showCodeInput && (
            <Controller
              name="code"
              control={control}
              rules={{
                required: t("invite.codeInput.required"),
                pattern: {
                  value: /^[A-Za-z0-9]+$/,
                  message: t("invite.codeInput.invalidFormat"),
                },
              }}
              render={({ field, fieldState }) => (
                <CustomLabelInput
                  label={t("invite.codeInput.label")}
                  id="invite-code"
                  error={!!fieldState.error}
                  required
                >
                  <TextField
                    {...field}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    placeholder={t("invite.codeInput.placeholder")}
                    size="small"
                    variant="outlined"
                    fullWidth
                    autoComplete="off"
                    inputProps={{
                      inputMode: "text",
                      pattern: "[A-Za-z0-9]*",
                      maxLength: 64,
                    }}
                  />
                </CustomLabelInput>
              )}
            />
          )}
            <Controller
              name="checkString"
              control={control}
              rules={{
                required: true,
                minLength: 1,
              }}
              render={({ field: { onChange, value }, fieldState }) => (
                <CustomLabelInput
                  label={t(
                    showCodeInput
                      ? "invite.checkStringLabel.withoutCode"
                      : "invite.checkStringLabel.withCode",
                  )}
                  required
                  error={!!fieldState.error}
                >
                  <Box display="flex" justifyContent="center">
                    <ReactCodeInput
                      type="text"
                      fields={3}
                      name="checkString"
                      inputMode="verbatim"
                      onChange={onChange}
                      value={value}
                    />
                  </Box>
                </CustomLabelInput>
              )}
            />
        </Stack>

        {errors.root && (
          <ValidationError>{t(`errors.${errors.root.type}`)}</ValidationError>
        )}

        <PublicForm.PublicFormActionContainer>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="contained"
            size="large"
            fullWidth
          >
            {t("invite.submitButton")}
          </Button>

          <PublicForm.PublicFormFooter>
            <Typography
              fontSize="14px"
              lineHeight="22px"
              fontWeight="400"
              color="#171A1FFF"
            >
              <Trans
                i18nKey="invite.helpLabel"
                values={{
                  contactHelpLink: t("invite.contactHelpLink"),
                }}
                components={{
                  anchor: (
                    <PublicForm.PublicFormLink
                      to={`mailto:${appConfig?.supportEmail}`}
                    />
                  ),
                }}
              />
            </Typography>
          </PublicForm.PublicFormFooter>
        </PublicForm.PublicFormActionContainer>
      </PublicForm.PublicFormContainer>
      <PublicForm.PublicFormFooter>
        <span>{t("signIn.noAccountLabel")}</span>
        <PublicForm.PublicFormLink to="/sign-up">
          {t("signIn.signUpLink")}
        </PublicForm.PublicFormLink>
        <PublicForm.PublicFormTos>
          <TermsOfService />
        </PublicForm.PublicFormTos>
      </PublicForm.PublicFormFooter>
    </PublicForm>
  );
};

export default InviteForm;
